import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

/**
 * SEO Component for managing meta tags, Open Graph, and Twitter Cards
 *
 * @param {Object} props
 * @param {string} props.title - Page title (defaults to site name)
 * @param {string} props.description - Page description
 * @param {string} props.image - OG image URL (should be absolute)
 * @param {string} props.type - OG type (website, article, etc.)
 * @param {string} props.url - Canonical URL (defaults to current location)
 * @param {string} props.siteName - Site name (defaults to "Strat Guys")
 * @param {string} props.twitterCard - Twitter card type (summary_large_image, summary)
 * @param {string} props.keywords - SEO keywords (comma-separated)
 * @param {string} props.author - Author name
 */
const SEO = ({
  title,
  description = 'Strat Guys - Your all-in-one gaming platform. Create tactical strategies, compete in Geoguesser challenges, and manage tournaments. Plan, play, and compete with precision.',
  image,
  type = 'website',
  url,
  siteName = 'Strat Guys',
  twitterCard = 'summary_large_image',
  keywords = 'strat guys, gaming platform, strategy planner, geoguesser, tournament tools, tactical planning, game maps, competitive gaming, collaborative planning',
  author = 'Strat Guys Team',
}) => {
  const location = useLocation();

  // Get base URL from environment or construct from window
  const getBaseUrl = () => {
    if (typeof window !== 'undefined') {
      return `${window.location.protocol}//${window.location.host}`;
    }
    // Fallback for SSR or build time
    return process.env.REACT_APP_SITE_URL || 'https://stratguys.com';
  };

  const baseUrl = getBaseUrl();
  const canonicalUrl = url || `${baseUrl}${location.pathname}${location.search}`;

  // Default OG image in frontend/public/og-image.jpg (ensure URL is absolute)
  let ogImage;
  if (image) {
    ogImage = image.startsWith('http') ? image : `${baseUrl}${image}`;
  } else {
    ogImage = `${baseUrl}/og-image.jpg`;
  }

  // Full title with site name
  const fullTitle = title ? `${title} | ${siteName}` : siteName;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="451" />
      <meta property="og:image:height" content="454" />
      <meta property="og:image:alt" content={title || siteName} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title || siteName} />
      <meta name="twitter:site" content="@stratguys" />
      <meta name="twitter:creator" content="@stratguys" />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#1a1a2e" />
      <meta name="msapplication-TileColor" content="#1a1a2e" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content={siteName} />

      {/* Article specific tags (if type is article) */}
      {type === 'article' && (
        <>
          <meta property="article:author" content={author} />
          <meta property="article:published_time" content={new Date().toISOString()} />
        </>
      )}
    </Helmet>
  );
};

export default SEO;
